import { useCallback, useEffect, useRef, useState } from "react";

const STEPS: Record<string, number> = { ArrowUp: -1, ArrowDown: 1 };

const EDITABLE_TAGS = ["INPUT", "TEXTAREA", "SELECT"];

const DIALOG_ROLES = ["dialog", "alertdialog"];

/** The drawer the rows open, which the arrows keep working inside of. */
const DRAWER_SLOT = "drawer-popup";

/**
 * Long enough that holding the key walks the list instead of reading one
 * record per row it passes over.
 */
const OPEN_DELAY = 120;

export type UseRowNavigationOptions = {
  /** How many rows the page currently holds. */
  rowCount: number;
  page: number;
  pageCount: number;
  /** Reads another page and resolves once its rows are in. */
  onPageChange: (page: number) => Promise<void>;
  /** What arriving on a row does, the drawer being what it opens. */
  onOpen: (index: number) => void;
};

export type UseRowNavigation = {
  /** The row the arrows are on, -1 while nothing is being walked. */
  activeIndex: number;
  /** Called when a row is reached by other means, a click or a close. */
  setActiveIndex: (index: number) => void;
};

/**
 * Walks the rows of a paginated table with the up and down arrows, crossing
 * page boundaries: the last row of a page steps onto the first of the next.
 *
 * The React counterpart of `DataTableRowNavigation`, which the legacy list
 * drives its quickview with. Nothing happens until a row is opened, so the
 * arrows belong to the page until someone starts reading records.
 */
export function useRowNavigation({
  rowCount,
  page,
  pageCount,
  onPageChange,
  onOpen,
}: UseRowNavigationOptions): UseRowNavigation {
  const [activeIndex, setActiveIndex] = useState(-1);

  // Everything the handler reads, kept current so it can be bound once. The
  // index is mirrored here because a keydown must read it, not schedule on it.
  const latest = useRef({
    activeIndex,
    rowCount,
    page,
    pageCount,
    onPageChange,
    onOpen,
  });

  // A page being read: the arrows wait rather than counting rows that are
  // about to be replaced.
  const movingRef = useRef(false);
  const openTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    latest.current = {
      activeIndex,
      rowCount,
      page,
      pageCount,
      onPageChange,
      onOpen,
    };
  });

  const focus = useCallback((index: number) => {
    clearTimeout(openTimeout.current);

    latest.current.activeIndex = index;
    setActiveIndex(index);
  }, []);

  useEffect(() => () => clearTimeout(openTimeout.current), []);

  useEffect(() => {
    function open(index: number) {
      focus(index);

      openTimeout.current = setTimeout(
        () => latest.current.onOpen(index),
        OPEN_DELAY,
      );
    }

    async function turnPage(step: number) {
      const { page: from, pageCount: total } = latest.current;
      const next = from + step;

      if (next < 1 || next > total) {
        return;
      }

      movingRef.current = true;

      try {
        await latest.current.onPageChange(next);

        open(step > 0 ? 0 : latest.current.rowCount - 1);
      } finally {
        movingRef.current = false;
      }
    }

    function handleKeydown(event: KeyboardEvent) {
      const step = STEPS[event.key];
      const current = latest.current.activeIndex;

      if (!step || current < 0 || movingRef.current) {
        return;
      }

      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }

      if (isTaken(event)) {
        return;
      }

      event.preventDefault();

      const next = current + step;

      if (next >= 0 && next < latest.current.rowCount) {
        open(next);

        return;
      }

      void turnPage(step);
    }

    // Capture, so a row being walked wins over whatever the focused control
    // would otherwise do with an arrow.
    document.addEventListener("keydown", handleKeydown, true);

    return () => document.removeEventListener("keydown", handleKeydown, true);
  }, [focus]);

  return { activeIndex, setActiveIndex: focus };
}

/**
 * Whether something else on the page has a better claim to the arrow: a field
 * being typed in, or a dialog over the list. The drawer is the exception, since
 * walking the rows is what it is open for.
 */
function isTaken(event: KeyboardEvent): boolean {
  const path = event.composedPath();

  const editable = path.some((node) => {
    const element = node as HTMLElement;

    return (
      EDITABLE_TAGS.includes(element.tagName) ||
      true === element.isContentEditable
    );
  });

  if (editable) {
    return true;
  }

  const inDrawer = path.some(
    (node) => (node as HTMLElement).getAttribute?.("data-slot") === DRAWER_SLOT,
  );

  if (inDrawer) {
    return false;
  }

  return path.some((node) =>
    DIALOG_ROLES.includes((node as HTMLElement).getAttribute?.("role") ?? ""),
  );
}
